import React, { ReactElement, useCallback, useMemo } from 'react'
import { MarkerProps, Marker as ReactMarker } from 'react-leaflet'
import MapTooltip from '../MapTooltip/MapTooltip'
import { useMapContext } from 'components/MapV2/MapProvider'
import { ThemeColorKeys } from '@evrekadev/evreka-ui-components'
import MarkerPopup, { PopupProps } from '../MapPopup/MapPopup'
import { getMarkerIcon } from './util'

export type MapIconType = ReactElement | 'ad-hoc'

export type MapMarkerProps = {
  id: string
  icon?: {
    idle?: MapIconType
    selected?: ReactElement
    size?: { idle: [number, number]; selected?: [number, number] }
    anchor?: {
      idle: [number, number]
      selected?: [number, number]
    }
  }
  badge?: { content: ReactElement; top?: number; right?: number; bottom?: number; left?: number }
  selectable?: boolean
  marker: MarkerProps
  clusterable?: boolean
  popup?: PopupProps
  isSelected?: boolean
  disablePopup?: boolean
  color?: ThemeColorKeys
  customColor?: string
  tooltip?: {
    text?: string
    permanent?: boolean
    offset?: [number, number]
  }
  'data-testid'?: string
}

const DEFAULT_MARKER_SIZE: [number, number] = [26, 30]
const DEFAULT_MARKER_ANCHOR: [number, number] = [13, 30]
const SELECTED_MARKER_SIZE: [number, number] = [40, 40]
const SELECTED_MARKER_ANCHOR: [number, number] = [20, 34]
const DEFAULT_MARKER_TOOLTIP_OFFSET: [number, number] = [0, -28]

export const MapMarker: React.FC<MapMarkerProps> = (props) => {
  const {
    icon,
    marker,
    tooltip,
    isSelected,
    badge,
    popup,
    disablePopup,
    color,
    customColor,
    'data-testid': dataTestId,
    id,
  } = props
  const { onMarkerSelect: onSelect, markerPopup, selectedMarkers } = useMapContext()

  const isMarkerInSelectedMarkers = useMemo(() => {
    return selectedMarkers?.some((selectedMarker) => selectedMarker.id === props.id)
  }, [selectedMarkers, props.id])

  const isMarkerSelected = isSelected ?? isMarkerInSelectedMarkers

  const handleOnSelect = useCallback(
    (marker: MapMarkerProps) => {
      onSelect?.(marker)
    },
    [onSelect],
  )

  const eventHandlers = useMemo(
    () => ({
      click: () => !disablePopup && handleOnSelect?.(props),
    }),
    [marker, handleOnSelect],
  )

  const popupConfig = markerPopup ?? popup

  const shownIcon = isMarkerSelected ? icon?.selected ?? icon?.idle : icon?.idle

  const shownSize: [number, number] =
    icon && icon.size
      ? isMarkerSelected
        ? icon.size.selected ?? icon.size.idle
        : icon.size.idle
      : isMarkerSelected
        ? SELECTED_MARKER_SIZE
        : DEFAULT_MARKER_SIZE

  const shownAnchor: [number, number] =
    icon && icon.anchor
      ? isMarkerSelected
        ? icon.anchor.selected ?? icon.anchor.idle
        : icon.anchor.idle
      : isMarkerSelected
        ? SELECTED_MARKER_ANCHOR
        : DEFAULT_MARKER_ANCHOR

  return (
    <ReactMarker
      icon={getMarkerIcon(
        id,
        shownIcon,
        shownSize,
        shownAnchor,
        isMarkerSelected,
        color,
        customColor,
        badge,
        dataTestId,
      )}
      position={marker.position}
      pmIgnore
      eventHandlers={eventHandlers}
      data-testid={dataTestId}
    >
      {!disablePopup && popupConfig && <MarkerPopup {...popupConfig} />}

      {tooltip && (
        <MapTooltip
          permanent={isMarkerSelected ? true : tooltip.permanent}
          direction={'top'}
          offset={tooltip.offset || DEFAULT_MARKER_TOOLTIP_OFFSET}
        >
          {tooltip.text}
        </MapTooltip>
      )}
    </ReactMarker>
  )
}
